/**
 * Ficha de producto DRIKVAR.
 *
 * Dos responsabilidades: cambiar de variante al elegir talla, y añadir al
 * carrito sin recargar.
 *
 * El alta va por la Cart AJAX API a propósito. Es lo que hace que Shopify
 * emita `product_added_to_cart`, el evento que el pixel de Meta necesita para
 * optimizar campañas. Un POST de formulario clásico no lo garantiza.
 * Ver docs/06-tema-shopify.md.
 *
 * El evento de carrito se emite con el mismo contrato que usa
 * assets/product-form.js de Horizon, para que el cajón y el contador del
 * header reaccionen igual que con sus propias secciones.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/*
 * `Theme` va desnudo, nunca `window.Theme`.
 *
 * snippets/scripts.liquid lo declara con `const` dentro de un script
 * clásico, y un `const` en el ámbito global NO se cuelga de `window`: vive
 * en el ámbito léxico global. Así que `Theme.routes` resuelve —también desde
 * un módulo— y `window.Theme` es undefined. Horizon lo usa desnudo en todo
 * su código; aquí igual.
 */

class FichaDrikvar {
  constructor(raiz) {
    this.raiz = raiz;
    this.form = raiz.querySelector('form[action*="/cart/add"]');
    this.entradaVariante = raiz.querySelector('[data-drikvar-variant-id]');
    this.boton = raiz.querySelector('[data-drikvar-submit]');

    // La etiqueta es un hijo del botón, no el botón. Escribir en
    // `boton.textContent` borraría el span que gira. Si por lo que sea no
    // está, se cae al propio botón y todo sigue funcionando sin animación.
    this.etiqueta = raiz.querySelector('[data-drikvar-label]') || this.boton;
    this.ahora = raiz.querySelector('[data-drikvar-now]');
    this.ahoraTexto = raiz.querySelector('.drikvar-pdp__now-text');
    this.precio = raiz.querySelector('[data-drikvar-price]');
    this.sku = raiz.querySelector('[data-drikvar-sku]');
    this.estado = raiz.querySelector('[data-drikvar-status]');

    this.cantidad = raiz.querySelector('[data-drikvar-qty]');
    this.aviso = raiz.querySelector('[data-drikvar-stock]');
    this.barra = raiz.querySelector('[data-drikvar-buy]');
    this.barraPrecio = raiz.querySelector('[data-drikvar-buy-price]');
    this.barraBoton = raiz.querySelector('[data-drikvar-buy-cta]');

    const datos = raiz.querySelector('[data-drikvar-variants]');
    this.variantes = datos ? JSON.parse(datos.textContent) : [];

    // Los ajustes del aviso sonoro viajan en el propio elemento. En un módulo
    // ES `document.currentScript` es null, así que no hay forma de leerlos
    // desde el script.
    const bloque = raiz.querySelector('[data-drikvar-sound]');
    this.sonido = bloque?.dataset.drikvarSound === 'on';
    this.volumen = Math.min(1, Math.max(0, Number(bloque?.dataset.drikvarVolume) || 0.2));
    this.audio = null;

    this.montarGaleria();
    this.montarZoom();
    this.montarTallas();

    if (!this.form) return;

    this.vigilarBarra();

    this.radios = [...raiz.querySelectorAll('.drikvar-options__input')];
    this.radios.forEach((r) => r.addEventListener('change', () => this.alCambiarOpcion()));
    this.form.addEventListener('submit', (e) => this.alEnviar(e));

    this.montarCantidad();
    this.marcarNoDisponibles();
    this.pintarStock(this.varianteActual());
  }

  /**
   * Cantidad. Los botones son de verdad, no flechitas del navegador: las
   * nativas miden 12px y en un móvil no se aciertan.
   */
  montarCantidad() {
    if (!this.cantidad) return;

    const mover = (paso) => {
      const tope = Number(this.cantidad.max) || Infinity;
      const nuevo = Math.min(tope, Math.max(1, (Number(this.cantidad.value) || 1) + paso));
      this.cantidad.value = String(nuevo);
      this.ajustarPasos();
    };

    this.raiz.querySelector('[data-drikvar-qty-menos]')?.addEventListener('click', () => mover(-1));
    this.raiz.querySelector('[data-drikvar-qty-mas]')?.addEventListener('click', () => mover(1));

    // Se puede escribir a mano, así que hay que sanear: un 0, un negativo o
    // texto pegado dejarían el formulario enviando una cantidad inválida.
    this.cantidad.addEventListener('change', () => {
      const tope = Number(this.cantidad.max) || Infinity;
      const valor = Math.min(tope, Math.max(1, Math.floor(Number(this.cantidad.value) || 1)));
      this.cantidad.value = String(valor);
      this.ajustarPasos();
    });

    this.ajustarPasos();
  }

  ajustarPasos() {
    if (!this.cantidad) return;
    const valor = Number(this.cantidad.value) || 1;
    const tope = Number(this.cantidad.max) || Infinity;
    const menos = this.raiz.querySelector('[data-drikvar-qty-menos]');
    const mas = this.raiz.querySelector('[data-drikvar-qty-mas]');
    if (menos) menos.disabled = valor <= 1;
    if (mas) mas.disabled = valor >= tope;
  }

  /**
   * Aviso de pocas unidades. Sale del inventario real, así que solo aparece
   * cuando Shopify lo gestiona y rechaza pedidos sin stock; en cualquier otro
   * caso `stock` llega nulo y no se dice nada.
   *
   * Deliberadamente no hay «N personas viendo esto»: ese número no lo mide
   * nadie, y una urgencia inventada se nota.
   */
  pintarStock(v) {
    if (!this.aviso) return;

    const umbral = Number(this.aviso.dataset.threshold) || 0;
    const quedan = v ? v.stock : null;
    const avisar = umbral > 0 && quedan != null && quedan > 0 && quedan <= umbral;

    this.aviso.hidden = !avisar;
    this.aviso.textContent = avisar ? (quedan === 1 ? 'Queda 1.' : `Quedan ${quedan}.`) : '';

    // La cantidad no puede pasarse del stock: pedir 5 de 3 se rechaza en el
    // carrito y esa corrección se lee como un fallo de la tienda.
    if (this.cantidad) {
      if (quedan != null && quedan > 0) {
        this.cantidad.max = String(quedan);
        if (Number(this.cantidad.value) > quedan) this.cantidad.value = String(quedan);
      } else {
        this.cantidad.removeAttribute('max');
      }
      this.ajustarPasos();
    }
  }

  /**
   * Galería.
   *
   * El desplazamiento y el enganche los hace el navegador con `scroll-snap`;
   * aquí solo se escucha en qué foto se ha parado para pintar el contador y
   * la miniatura activa, y se ofrecen atajos —miniaturas y flechas— que
   * mueven la pista.
   *
   * Es mucho más robusto que un visor propio: el arrastre en móvil llega con
   * su inercia y su rebote, la rueda horizontal funciona, y no hay que
   * perseguir el estado de un gesto a mano.
   */
  montarGaleria() {
    this.galeria = this.raiz.querySelector('[data-drikvar-gallery]');
    if (!this.galeria) return;

    this.pista = this.galeria.querySelector('[data-drikvar-gal-track]');
    this.slides = [...this.galeria.querySelectorAll('[data-drikvar-gal-slide]')];
    this.miniaturas = [...this.galeria.querySelectorAll('[data-drikvar-gal-thumb]')];
    this.contador = this.galeria.querySelector('[data-drikvar-gal-count]');
    this.indice = 0;

    if (this.slides.length < 2) return;

    this.miniaturas.forEach((mini, i) => mini.addEventListener('click', () => this.irA(i)));

    this.galeria
      .querySelector('[data-drikvar-gal-prev]')
      ?.addEventListener('click', () => this.irA(this.indice - 1));
    this.galeria
      .querySelector('[data-drikvar-gal-next]')
      ?.addEventListener('click', () => this.irA(this.indice + 1));

    // El scroll se lee al final del gesto, no durante: repintar el contador
    // en cada píxel no aporta nada y obliga a medir todo el rato.
    let reposo;
    this.pista.addEventListener(
      'scroll',
      () => {
        clearTimeout(reposo);
        reposo = setTimeout(() => this.marcarFoto(Math.round(this.pista.scrollLeft / this.pista.clientWidth)), 90);
      },
      { passive: true }
    );
  }

  irA(i) {
    const total = this.slides.length;
    // Da la vuelta por los dos lados: llegar al final y quedarse bloqueado
    // obliga a arrastrar hacia atrás toda la tira.
    const destino = (i + total) % total;
    this.pista.scrollTo({ left: destino * this.pista.clientWidth, behavior: 'smooth' });
    this.marcarFoto(destino);
  }

  marcarFoto(i) {
    if (i === this.indice || i < 0 || i >= this.slides.length) return;
    this.indice = i;

    this.miniaturas.forEach((mini, n) => mini.setAttribute('aria-selected', String(n === i)));

    if (this.contador) {
      const n = String(i + 1).padStart(2, '0');
      const total = String(this.slides.length).padStart(2, '0');
      this.contador.textContent = `${n} / ${total}`;
    }
  }

  /**
   * Zoom. Un solo <dialog> que se rellena con la foto pulsada, en lugar de
   * uno por imagen: así las versiones a 2400px se piden solo cuando alguien
   * abre de verdad, y no cinco de golpe al cargar la ficha.
   */
  montarZoom() {
    const zoom = this.raiz.querySelector('[data-drikvar-zoom]');
    if (!zoom) return;

    const img = document.createElement('img');
    img.className = 'drikvar-zoom__img';
    img.alt = '';
    zoom.querySelector('[data-drikvar-zoom-slot]').appendChild(img);

    // Solo las diapositivas con `data-full` abren el zoom, es decir, las de
    // imagen. Un vídeo tiene sus propios controles y abrirlo a pantalla
    // grande al pulsar play sería justo lo contrario de lo que se pidió.
    this.raiz.querySelectorAll('[data-drikvar-gal-slide][data-full]').forEach((slide) => {
      slide.addEventListener('click', () => {
        img.src = slide.dataset.full;
        img.alt = slide.dataset.alt || '';
        zoom.removeAttribute('data-zoomed');
        img.style.transformOrigin = 'center center';
        zoom.showModal();
      });
    });

    zoom.querySelector('[data-drikvar-zoom-close]')?.addEventListener('click', () => zoom.close());

    // Al acercar, el punto pulsado es el que se queda quieto: si se ampliara
    // siempre por el centro, para ver una costura del borde habría que
    // acercar y luego buscarla.
    img.addEventListener('click', (evento) => {
      if (zoom.hasAttribute('data-zoomed')) {
        zoom.removeAttribute('data-zoomed');
        return;
      }
      const caja = img.getBoundingClientRect();
      const x = ((evento.clientX - caja.left) / caja.width) * 100;
      const y = ((evento.clientY - caja.top) / caja.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
      zoom.setAttribute('data-zoomed', '');
    });

    // Con el puntero encima y ya ampliado, mover el ratón recorre la foto.
    img.addEventListener('pointermove', (evento) => {
      if (!zoom.hasAttribute('data-zoomed') || evento.pointerType !== 'mouse') return;
      const caja = img.getBoundingClientRect();
      const x = ((evento.clientX - caja.left) / caja.width) * 100;
      const y = ((evento.clientY - caja.top) / caja.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    });

    zoom.addEventListener('click', (evento) => {
      if (evento.target === zoom) zoom.close();
    });
  }

  montarTallas() {
    const guia = this.raiz.querySelector('[data-drikvar-sizes]');
    if (!guia) return;

    this.raiz.querySelectorAll('[data-drikvar-sizes-open]').forEach((boton) => {
      boton.addEventListener('click', () => guia.showModal());
    });
    guia.querySelector('[data-drikvar-sizes-close]')?.addEventListener('click', () => guia.close());
    guia.addEventListener('click', (evento) => {
      if (evento.target === guia) guia.close();
    });
  }

  /**
   * La barra de compra aparece cuando el botón de verdad se ha ido de la
   * pantalla, y se retira al llegar al pie.
   *
   * Lo del pie no es un detalle estético: la barra taparía los enlaces
   * legales, y Meta exige que la política de privacidad y las condiciones de
   * devolución sean alcanzables para aprobar el catálogo.
   *
   * Se usa IntersectionObserver y no el evento de scroll porque el navegador
   * lo resuelve fuera del hilo principal: con scroll habría que medir en cada
   * fotograma y el desplazamiento se nota.
   */
  vigilarBarra() {
    if (!this.barra || !('IntersectionObserver' in window)) return;

    const pie = document.querySelector('footer');
    let botonFuera = false;
    let pieDentro = false;

    const pintar = () => this.barra.toggleAttribute('data-visible', botonFuera && !pieDentro);

    new IntersectionObserver(
      ([entrada]) => {
        botonFuera = !entrada.isIntersecting;
        pintar();
      },
      { rootMargin: '0px 0px -80px 0px' }
    ).observe(this.boton);

    if (pie) {
      new IntersectionObserver(([entrada]) => {
        pieDentro = entrada.isIntersecting;
        pintar();
      }).observe(pie);
    }
  }

  /** Valores elegidos, en el orden de las opciones del producto. */
  seleccion() {
    return [...this.raiz.querySelectorAll('.drikvar-options')].map((grupo) => {
      const marcado = grupo.querySelector('.drikvar-options__input:checked');
      return marcado ? marcado.value : null;
    });
  }

  varianteActual() {
    const elegido = this.seleccion();
    if (!elegido.length) return this.variantes[0];
    return this.variantes.find((v) => v.options.every((op, i) => op === elegido[i]));
  }

  alCambiarOpcion() {
    const v = this.varianteActual();
    this.marcarNoDisponibles();

    if (!v) {
      // Combinación que no existe como variante: no se puede comprar.
      this.boton.disabled = true;
      this.etiqueta.textContent = 'No disponible';
      if (this.ahora) this.ahora.disabled = true;
      if (this.barraBoton) {
        this.barraBoton.disabled = true;
        this.barraBoton.textContent = 'No disponible';
      }
      return;
    }

    this.entradaVariante.value = v.id;

    if (this.precio) {
      const actual = this.precio.querySelector('.drikvar-pdp__price-current');
      const antes = this.precio.querySelector('.drikvar-pdp__price-was');
      if (actual) actual.textContent = v.price;
      if (antes) {
        antes.textContent = v.compare_at || '';
        antes.hidden = !v.compare_at;
      }
    }

    if (this.sku) this.sku.textContent = v.sku ? `SKU ${v.sku}` : '';

    this.pintarStock(v);

    // La galería salta a la foto de la variante si la tiene. Es lo que se
    // espera al elegir un color; con tallas normalmente no hay foto propia y
    // entonces no se mueve nada.
    if (v.imagen != null && this.pista) this.irA(v.imagen);

    this.boton.disabled = !v.available;
    this.etiqueta.textContent = v.available ? 'Añadir al carrito' : 'Agotado';
    if (this.ahora) this.ahora.disabled = !v.available;

    // La barra fija refleja lo mismo: si enseñara otro precio u otro estado
    // que el panel, se convierte en un motivo para desconfiar justo en el
    // momento de pulsar.
    if (this.barraPrecio) this.barraPrecio.textContent = v.price;
    if (this.barraBoton) {
      this.barraBoton.disabled = !v.available;
      this.barraBoton.textContent = v.available ? 'Añadir' : 'Agotado';
    }

    // Deja la variante en la URL para que el enlace se pueda compartir y para
    // que al recargar se conserve la elección.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    window.history.replaceState({}, '', url);
  }

  /**
   * Tacha los valores que, combinados con lo ya elegido, no existen o están
   * agotados. Solo mira el último eje para no bloquear al usuario.
   */
  marcarNoDisponibles() {
    const elegido = this.seleccion();

    this.raiz.querySelectorAll('.drikvar-options').forEach((grupo, indice) => {
      grupo.querySelectorAll('.drikvar-options__input').forEach((radio) => {
        const prueba = [...elegido];
        prueba[indice] = radio.value;

        const existe = this.variantes.some(
          (v) => v.available && v.options.every((op, i) => prueba[i] == null || op === prueba[i])
        );

        const chip = grupo.querySelector(`label[for="${radio.id}"]`);
        if (chip) chip.toggleAttribute('data-unavailable', !existe);
      });
    });
  }

  /**
   * Aviso sonoro al añadir. Un golpe seco y un filo, ~150 ms.
   *
   * Se sintetiza con Web Audio en vez de descargar un archivo. Un mp3 corto
   * son 15-30 KB que se piden sí o sí, y casi nadie llega a oírlos: esto pesa
   * cero bytes y suena solo cuando hace falta.
   *
   * El contexto se crea en el primer clic y no antes. Los navegadores no
   * dejan arrancar audio sin un gesto del visitante, y crearlo al cargar la
   * página lo dejaría suspendido de todas formas.
   *
   * Dos tonos, no una campanita: un cuerpo grave que cae de 190 a 120 Hz y un
   * filo agudo justo detrás. Es el sonido de algo que se cierra, no de una
   * notificación alegre.
   */
  sonar() {
    if (!this.sonido) return;

    try {
      const Contexto = window.AudioContext || window.webkitAudioContext;
      if (!Contexto) return;

      this.audio = this.audio || new Contexto();
      if (this.audio.state === 'suspended') this.audio.resume();

      const t0 = this.audio.currentTime;
      const maestro = this.audio.createGain();
      maestro.gain.value = this.volumen;
      maestro.connect(this.audio.destination);

      /**
       * @param {number} hz - Frecuencia de salida.
       * @param {number} hasta - A dónde cae.
       * @param {OscillatorType} tipo - Forma de onda.
       * @param {number} desde - Retraso desde el inicio, en segundos.
       * @param {number} largo - Duración, en segundos.
       * @param {number} pico - Amplitud máxima, 0 a 1.
       */
      const golpe = (hz, hasta, tipo, desde, largo, pico) => {
        const osc = this.audio.createOscillator();
        const vol = this.audio.createGain();

        osc.type = tipo;
        osc.frequency.setValueAtTime(hz, t0 + desde);
        osc.frequency.exponentialRampToValueAtTime(hasta, t0 + desde + largo);

        // Rampas exponenciales, no lineales: el oído percibe el volumen en
        // escala logarítmica, y una caída lineal se oye como un corte.
        // Nunca a cero exacto —exponentialRamp no lo admite—, de ahí 0.0001.
        vol.gain.setValueAtTime(0.0001, t0 + desde);
        vol.gain.exponentialRampToValueAtTime(pico, t0 + desde + 0.006);
        vol.gain.exponentialRampToValueAtTime(0.0001, t0 + desde + largo);

        osc.connect(vol).connect(maestro);
        osc.start(t0 + desde);
        osc.stop(t0 + desde + largo + 0.02);
      };

      golpe(190, 120, 'triangle', 0, 0.1, 1);
      golpe(1250, 900, 'sine', 0.045, 0.055, 0.32);
    } catch {
      // Sin audio disponible no pasa nada: el aviso visual y el número del
      // carrito ya cuentan lo que ha ocurrido.
    }
  }

  async alEnviar(evento) {
    evento.preventDefault();

    const v = this.varianteActual();
    if (!v || !v.available) return;

    // Qué botón envió el formulario. «Comprar ahora» lleva `name="return_to"`,
    // que es lo que hace la redirección sin JavaScript; aquí se usa la misma
    // marca para decidir a dónde ir después.
    const alCarrito = evento.submitter === this.ahora;

    // El texto de reposo se deduce de la variante, no se lee del botón. Si se
    // leyera, pulsar otra vez mientras pone «Añadido» guardaría esa palabra
    // como estado normal y el botón se quedaría diciendo «Añadido» para
    // siempre.
    const reposo = v.available ? 'Añadir al carrito' : 'Agotado';

    // Turno. Un segundo clic invalida el acuse del primero: sin esto, el
    // temporizador viejo devolvería la etiqueta a reposo justo cuando el
    // nuevo está poniendo «Añadiendo…».
    this.turno = (this.turno || 0) + 1;
    const turno = this.turno;

    // La barra de compra fija envía este mismo formulario con el atributo
    // `form`, así que hereda la cantidad sin código propio. Por eso se montó
    // apuntando al formulario en vez de con su propia petición.
    const cuantos = Math.max(1, Math.floor(Number(this.cantidad?.value) || 1));

    this.boton.disabled = true;
    if (this.ahora) this.ahora.disabled = true;

    // Con «Comprar ahora» se avisa en su propio botón: cambiar el de arriba
    // señalaría al sitio equivocado.
    const enCurso = alCarrito && this.ahoraTexto ? this.ahoraTexto : this.etiqueta;
    const enCursoReposo = enCurso === this.etiqueta ? reposo : enCurso.textContent;
    enCurso.textContent = 'Añadiendo…';

    const originalBarra = this.barraBoton ? this.barraBoton.textContent : null;
    if (this.barraBoton) {
      this.barraBoton.disabled = true;
      this.barraBoton.textContent = 'Añadiendo…';
    }

    const diferido = CartLinesUpdateEvent.createPromise();

    // Se anuncia antes de que termine la petición, igual que Horizon: los
    // componentes que escuchan esperan a la promesa.
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: v.id, quantity: cuantos }],
        promise: diferido.promise,
      })
    );

    let logrado = false;

    try {
      const alta = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: cuantos }] }),
      });
      const cuerpo = await alta.json();

      if (cuerpo.status) throw new Error(cuerpo.description || cuerpo.message || 'No se pudo añadir');

      const carrito = await (await fetch(`${Theme.routes.cart_url}.js`)).json();

      // El contrato del evento es `{ cart, detail }`, no el carrito pelado.
      // El icono de la cabecera lee `cart.totalQuantity ?? detail.itemCount`:
      // resolviendo con el objeto de /cart.js tal cual, las dos salen
      // `undefined`, el icono cae a su valor por defecto —cero— y la burbuja
      // se esconde en vez de subir. Por eso el número solo se movía al tocar
      // el cajón, que sí resuelve con la forma correcta.
      diferido.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
        detail: {
          items: carrito.items,
          itemCount: carrito.item_count,
          productId: this.raiz.querySelector('[name="product-id"]')?.value,
          source: 'drikvar-product',
          didError: false,
        },
      });
      logrado = true;

      if (this.estado) this.estado.textContent = 'Producto añadido al carrito.';
    } catch (error) {
      diferido.reject(error);
      document.dispatchEvent(
        new CartErrorEvent({ error: error.message, code: 'INVALID' })
      );
      if (this.estado) this.estado.textContent = 'No se pudo añadir el producto.';
    } finally {
      // Se reactiva en cuanto responde el servidor, no cuando acaba el acuse.
      // Si esperara a la animación, la barra fija se quedaría diciendo
      // «Añadiendo…» segundo y medio después de haber añadido.
      this.boton.disabled = false;
      if (this.ahora) this.ahora.disabled = false;
      if (this.barraBoton) {
        this.barraBoton.disabled = false;
        this.barraBoton.textContent = originalBarra;
      }

      // La etiqueta la devuelve el acuse cuando sale bien. Aquí solo se
      // rescata si falló, o se quedaría en «Añadiendo…» para siempre.
      if (!logrado) enCurso.textContent = enCursoReposo;
    }

    if (!logrado) return;

    // Con «Comprar ahora» no se celebra nada: se va al carrito. Una animación
    // de acuse antes de una navegación solo retrasa el viaje.
    if (alCarrito) {
      window.location.href = Theme.routes.cart_url;
      return;
    }

    // El acuse queda fuera del try a propósito: la compra ya está hecha y aquí
    // no hay nada que pueda fallar de forma que importe.
    this.sonar();
    this.etiqueta.textContent = 'Añadido';

    // Se queda un momento en «Añadido» antes de volver. Sin esa pausa el acuse
    // pasa tan rápido que no da tiempo a leerlo.
    await new Promise((listo) => setTimeout(listo, 1400));

    // Otro clic mientras tanto: manda el nuevo, éste se calla.
    if (turno !== this.turno) return;
    this.etiqueta.textContent = reposo;
  }
}

document.querySelectorAll('.drikvar-pdp').forEach((pdp) => {
  // La raíz es la sección entera: el JSON de variantes es hermano del bloque.
  new FichaDrikvar(pdp.closest('.shopify-section') || document);
});
